import type { ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';
import { lazyPage } from './lib/lazyPage';
import { DEMO_MODE } from './config/demo';
import { AppProvider } from './context/AppContext';
import { AppShell } from './components/layout/AppShell';
import { TourProvider } from './components/tour/Tour';
import { RequireClinicRole } from './components/layout/RequireClinicRole';
import Login from './pages/Login';
import Welcome from './pages/Welcome';
import ResetPassword from './pages/ResetPassword';
import { RequireSuperAdmin } from './components/layout/RequireSuperAdmin';
import { RequireRoles } from './components/layout/RequireRoles';

const Home = lazyPage(() => import('./pages/Home'));
const TodayBoard = lazyPage(() => import('./pages/TodayBoard'));
const TodayList = lazyPage(() => import('./pages/TodayList'));
const TodayOld = lazyPage(() => import('./pages/Today'));
const TwoStep = lazyPage(() => import('./pages/TwoStep'));
const CodePreview = lazyPage(() => import('./pages/CodePreview'));
const Placeholder = lazyPage(() => import('./pages/Placeholder'));
const Patients = lazyPage(() => import('./pages/Patients'));
const QuickAdd = lazyPage(() => import('./pages/QuickAdd'));
const ScanCard = lazyPage(() => import('./pages/ScanCard'));
const PatientDetailPage = lazyPage(() => import('./pages/PatientDetail'));
const Attendance = lazyPage(() => import('./pages/Attendance'));
const Facilities = lazyPage(() => import('./pages/Facilities'));
const FacilityWizard = lazyPage(() => import('./pages/FacilityWizard'));
const FacilityDetail = lazyPage(() => import('./pages/FacilityDetail'));
const VisitSlots = lazyPage(() => import('./pages/VisitSlots'));
const Flags = lazyPage(() => import('./pages/Flags'));
const CallDetail = lazyPage(() => import('./pages/CallDetail'));
const Staff = lazyPage(() => import('./pages/Staff'));
const ClinicSettings = lazyPage(() => import('./pages/ClinicSettings'));
const ScriptSignoff = lazyPage(() => import('./pages/ScriptSignoff'));
const SystemHealth = lazyPage(() => import('./pages/SystemHealth'));
const AgentsLibrary = lazyPage(() => import('./pages/AgentsLibrary'));
const DefaultProtocols = lazyPage(() => import('./pages/DefaultProtocols'));
const AuditLog = lazyPage(() => import('./pages/AuditLog'));
const ScheduleEditor = lazyPage(() => import('./pages/ScheduleEditor'));
const Reports = lazyPage(() => import('./pages/Reports'));
const AiScribe = lazyPage(() => import('./pages/AiScribe'));
const VisitPage = lazyPage(() => import('./pages/VisitPage'));
const PrescriptionTemplates = lazyPage(() => import('./pages/PrescriptionTemplates'));
const TemplateEditor = lazyPage(() => import('./pages/TemplateEditor'));
const Guardrails = lazyPage(() => import('./pages/Guardrails'));
const Calls = lazyPage(() => import('./pages/Calls'));

const clinic = (el: ReactNode) => <RequireClinicRole>{el}</RequireClinicRole>;
const platform = (el: ReactNode) => <RequireSuperAdmin>{el}</RequireSuperAdmin>;

export default function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <TourProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/welcome" element={<Welcome />} />
          <Route path="/reset" element={<ResetPassword />} />
          {DEMO_MODE && <Route path="/sign-in-code" element={<CodePreview />} />}
          <Route path="/c/:slug/login" element={<Login />} />
          <Route element={<AppShell />}>
            <Route index element={<Home />} />
            {/* Earlier Today designs, kept for a week after the switch (2 Nov 2026). */}
            <Route path="today-new" element={clinic(<TodayBoard />)} />
            <Route path="today-list" element={clinic(<TodayList />)} />
            <Route path="today-old" element={clinic(<TodayOld />)} />
            <Route path="two-step" element={<TwoStep />} />
            <Route path="patients" element={clinic(<Patients />)} />
            <Route path="patients/new" element={clinic(<QuickAdd />)} />
            <Route path="patients/scan" element={clinic(<ScanCard />)} />
            <Route path="patients/:id" element={clinic(<PatientDetailPage />)} />
            <Route path="patients/:id/visits/:visitId" element={clinic(<VisitPage />)} />
            <Route path="prescriptions" element={<RequireRoles roles={['doctor']}><PrescriptionTemplates /></RequireRoles>} />
            <Route path="prescriptions/:tid" element={<RequireRoles roles={['doctor']}><TemplateEditor /></RequireRoles>} />
            <Route path="attendance" element={clinic(<Attendance />)} />
            <Route path="facilities" element={platform(<Facilities />)} />
            <Route path="facilities/new" element={platform(<FacilityWizard />)} />
            <Route path="facilities/:id" element={platform(<FacilityDetail />)} />
            <Route path="facilities/:id/edit" element={platform(<FacilityWizard />)} />
            <Route path="flags" element={clinic(<Flags />)} />
            <Route path="calls" element={clinic(<Calls />)} />
            <Route path="calls/:id" element={clinic(<CallDetail />)} />
            <Route path="staff" element={<RequireRoles roles={['facility_admin', 'doctor']}><Staff /></RequireRoles>} />
            <Route path="slots" element={<RequireRoles roles={['staff', 'facility_admin', 'doctor']}><VisitSlots /></RequireRoles>} />
            <Route path="settings" element={<RequireRoles roles={['facility_admin', 'doctor']}><ClinicSettings /></RequireRoles>} />
            <Route path="scripts" element={<RequireRoles roles={['facility_admin', 'doctor']}><ScriptSignoff /></RequireRoles>} />
            <Route path="system-health" element={platform(<SystemHealth />)} />
            <Route path="agents" element={platform(<AgentsLibrary />)} />
            <Route path="protocols" element={platform(<DefaultProtocols />)} />
            <Route path="audit" element={platform(<AuditLog />)} />
            <Route path="guardrails" element={platform(<Guardrails />)} />
            <Route path="safety-review" element={<Navigate to="/calls?tab=review" replace />} />
            <Route path="schedule" element={<RequireRoles roles={['facility_admin', 'doctor']}><ScheduleEditor /></RequireRoles>} />
            <Route path="reports" element={<RequireRoles roles={['facility_admin', 'doctor']}><Reports /></RequireRoles>} />
            <Route path="scribe" element={<RequireRoles roles={['facility_admin', 'doctor']}><AiScribe /></RequireRoles>} />
            <Route path="*" element={<Placeholder />} />
          </Route>
        </Routes>
        </TourProvider>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </AppProvider>
  );
}
